import React from "react"
import { useTranslation } from "react-i18next"
import { StyleSheet, View } from "react-native"

import { getRelativeTimeParams } from "@/base/format-relative-time"
import {
  formatInstallationType,
  isCenterPivotInstallationType,
  isReelInstallationType,
  isTravelerInstallationType,
} from "@/base/sensor-configurations"
import {
  AdminInfo,
  BatteryCriticalWarning,
  DeviceCommentDisplay,
  DeviceOfflineBadge,
  DeviceUnreachableWarning,
  LastCheckInText,
  NextScheduledActionText,
  StatusBadge,
  StatusBadges,
} from "@/components/DeviceListItem"
import { RunProgressBar } from "@/components/RunProgressBar"
import {
  AppIcons,
  AppText,
  Box,
  Button,
  COLORS,
  Collapse,
  Divider,
  Pressable,
  Row,
  SPACING,
  useIsDarkMode,
} from "@/theme"

import type { InstallationType } from "@/base/sensor-configurations"
import type {
  DeviceStatusData,
  SensorStatusBadges,
} from "@/redux/device-event-last.reducer"

import {
  getDeviceRowState,
  selectPrimaryStatusBadgeKeys,
} from "./DeviceListItemCondensed.helpers"
import type {
  DeviceRowState,
  PrimaryStatusBadgeKey,
} from "./DeviceListItemCondensed.helpers"

const DOT_SIZE = 10
const ROW_VERTICAL_PADDING = SPACING.$2

interface StatusDotProps {
  isDark: boolean
  state: DeviceRowState
}

function getDotColor(isDark: boolean, state: DeviceRowState): string {
  switch (state) {
    case "stoppedShort":
    case "batteryCritical": {
      return COLORS.$red[500]
    }
    case "unreachable":
    case "unconfigured": {
      return COLORS.$amber[isDark ? 400 : 500]
    }
    case "offline":
    case "idle": {
      return isDark ? COLORS.$gray[500] : COLORS.$gray[400]
    }
    case "online":
    default: {
      return COLORS.$emerald[isDark ? 400 : 500]
    }
  }
}

function StatusDot({ isDark, state }: StatusDotProps): React.JSX.Element {
  const backgroundColor = getDotColor(isDark, state)
  // Hollow ring distinguishes "offline" (no comms) from "idle" (online but
  // not currently doing anything). Both use a gray fill color.
  const isHollow = state === "offline"

  return (
    <View
      accessibilityLabel={state}
      style={[
        styles.dot,
        isHollow ? styles.dotHollow : null,
        isHollow ? { borderColor: backgroundColor } : { backgroundColor },
      ]}
    />
  )
}

export interface CompactRowProps {
  badges: SensorStatusBadges
  deviceEventTimestamp: string | undefined
  deviceInstallationType: InstallationType
  deviceName: string
  isDark: boolean
  /**
   * When provided, replaces the default per-installation-type badge selection.
   * Lets composite rows (e.g. the unified center-pivot row) inject their own
   * merged badge set.
   */
  primaryBadgeKeysOverride?: PrimaryStatusBadgeKey[]
  runCompletionPct: number | undefined
  /**
   * Optional override for the secondary line under the device name. When
   * omitted, falls back to the installation type label.
   */
  secondaryText?: string
  state: DeviceRowState
}

export function CompactRow({
  badges,
  deviceEventTimestamp,
  deviceInstallationType,
  deviceName,
  isDark,
  primaryBadgeKeysOverride,
  runCompletionPct,
  secondaryText,
  state,
}: CompactRowProps): React.JSX.Element {
  const { t } = useTranslation("devices")

  const asOfParams = React.useMemo(
    () =>
      typeof deviceEventTimestamp === "string"
        ? getRelativeTimeParams(deviceEventTimestamp)
        : undefined,
    [deviceEventTimestamp],
  )

  const primaryBadgeKeys = React.useMemo(
    () =>
      primaryBadgeKeysOverride ??
      selectPrimaryStatusBadgeKeys({ badges, deviceInstallationType }),
    [badges, deviceInstallationType, primaryBadgeKeysOverride],
  )

  const typeLabel = secondaryText ?? formatInstallationType(deviceInstallationType)

  const showCompletionPct =
    typeof runCompletionPct === "number" &&
    (isTravelerInstallationType(deviceInstallationType) ||
      isReelInstallationType(deviceInstallationType) ||
      isCenterPivotInstallationType(deviceInstallationType))

  // Right-hand status content varies by device state.
  let rightContent: React.JSX.Element

  if (state === "stoppedShort") {
    rightContent = (
      <Row alignItems={"center"} justifyContent={"flex-end"} space={"$1"}>
        <AppIcons.Warning color={COLORS.$red[isDark ? 400 : 600]} size={14} />
        <AppText
          fontSize={"$xs"}
          style={
            isDark ? styles.batteryCriticalTextDark : styles.batteryCriticalText
          }
        >
          {t("runStoppedShort")}
        </AppText>
      </Row>
    )
  } else if (state === "offline") {
    rightContent = (
      <Row alignItems={"center"} justifyContent={"flex-end"} space={"$1"}>
        <AppIcons.DeviceOffline size={14} />
        <AppText colorScheme={"secondary"} fontSize={"$xs"}>
          {t("deviceOffline")}
        </AppText>
      </Row>
    )
  } else if (state === "unconfigured") {
    rightContent = (
      <Row alignItems={"center"} justifyContent={"flex-end"} space={"$1"}>
        <AppIcons.Warning
          color={COLORS.$amber[isDark ? 400 : 600]}
          size={14}
        />
        <AppText
          fontSize={"$xs"}
          style={isDark ? styles.unconfiguredTextDark : styles.unconfiguredText}
        >
          {t("unconfigured", { defaultValue: "Unconfigured", ns: "common" })}
        </AppText>
      </Row>
    )
  } else if (state === "batteryCritical") {
    rightContent = (
      <Row alignItems={"center"} justifyContent={"flex-end"} space={"$1"}>
        <AppIcons.Battery color={COLORS.$red[isDark ? 400 : 600]} size={14} />
        <AppText
          fontSize={"$xs"}
          style={
            isDark ? styles.batteryCriticalTextDark : styles.batteryCriticalText
          }
        >
          {t("BES.short", {
            defaultValue: "Low battery",
            ns: "sensorStates",
          })}
        </AppText>
      </Row>
    )
  } else {
    rightContent = (
      <Row alignItems={"center"} justifyContent={"flex-end"} space={"$1"}>
        {showCompletionPct && typeof runCompletionPct === "number" ? (
          <RunPercentBadge runCompletionPct={runCompletionPct} />
        ) : null}
        {primaryBadgeKeys.map((key) => {
          const badge = badges[key]
          if (!badge) {
            return null
          }
          return (
            <StatusBadge
              key={String(key)}
              {...badge}
              fontSize={"$xs"}
              style={styles.compactBadge}
            />
          )
        })}
      </Row>
    )
  }

  return (
    <Row alignItems={"center"} flexWrap={"nowrap"}>
      <Box mr={"$2"}>
        <StatusDot isDark={isDark} state={state} />
      </Box>
      <Box flex={1} mr={"$2"} style={styles.nameColumn}>
        <AppText isTruncated fontSize={"$md"} style={styles.nameText}>
          {deviceName}
        </AppText>
        <AppText
          isTruncated
          colorScheme={"secondary"}
          fontSize={"$sm"}
          numberOfLines={1}
        >
          {typeLabel}
        </AppText>
      </Box>
      <Box alignItems={"flex-end"} style={styles.rightColumn}>
        {rightContent}
        {asOfParams ? (
          <AppText
            colorScheme={"secondary"}
            fontSize={"$xs"}
            numberOfLines={1}
            style={styles.asOfText}
          >
            {t("asOfRelativeTime", { ...asOfParams })}
          </AppText>
        ) : null}
      </Box>
    </Row>
  )
}

function RunPercentBadge({
  runCompletionPct,
}: {
  runCompletionPct: number
}): React.JSX.Element {
  const { t } = useTranslation()
  return (
    <Box>
      <Box style={styles.runPercentBadge}>
        <AppText fontSize={"$xs"} style={styles.runPercentBadgeText}>
          {t("percentWithVal", {
            ns: "common",
            val: Math.round(runCompletionPct),
          })}
        </AppText>
      </Box>
    </Box>
  )
}

export interface DeviceListItemCondensedProps {
  data: DeviceStatusData
  isExpanded: boolean
  isSelected?: boolean
  onOpenProfile: () => void
  /**
   * Action when the row body is pressed. If omitted, defaults to
   * `onToggleExpanded` — useful on screens like the devices index where
   * tap-to-expand is the primary interaction. The sidebar overrides this
   * to perform map selection while the chevron handles expand.
   */
  onPressRow?: () => void
  onToggleExpanded: () => void
  showAdminInfo?: boolean
  showComment?: boolean
}

/**
 * Single-line collapsed device row with an expandable detail panel.
 *
 * Two pressable hit targets:
 *   - row body: runs `onPressRow` (defaults to `onToggleExpanded`)
 *   - chevron: always runs `onToggleExpanded`
 *
 * The expanded panel reuses the existing DeviceListItem sub-components
 * (StatusBadges, NextScheduledActionText, BatteryCriticalWarning, etc.) so
 * no rendering logic is duplicated.
 *
 * Works on web and native: animation uses LayoutAnimation via the shared
 * `Collapse` theme component which already enables the experimental flag
 * on Android.
 */
export function DeviceListItemCondensed({
  data,
  isExpanded,
  isSelected = false,
  onOpenProfile,
  onPressRow,
  onToggleExpanded,
  showAdminInfo = false,
  showComment = false,
}: DeviceListItemCondensedProps): React.JSX.Element {
  const { t } = useTranslation("devices")
  const isDark = useIsDarkMode()
  const handlePressRow = onPressRow ?? onToggleExpanded

  const state = getDeviceRowState(data)

  const accentColor = COLORS.$buttonPrimary.light // matches the existing accent
  const rowStyle = [
    styles.row,
    isSelected
      ? [
          styles.rowSelected,
          {
            backgroundColor: isDark ? COLORS.$gray[800] : COLORS.$amber[100],
            borderLeftColor: accentColor,
          },
        ]
      : styles.rowUnselected,
  ]

  const chevronLabel = isExpanded
    ? t("collapseDeviceRow", { defaultValue: "Collapse device details" })
    : t("expandDeviceRow", { defaultValue: "Expand device details" })

  return (
    <View style={rowStyle}>
      <Row alignItems={"center"} flexWrap={"nowrap"}>
        <Box flex={1}>
          <Pressable
            accessibilityLabel={data.deviceName}
            id={`deviceListItemCondensed_${data.codaDeviceAlias}`}
            style={styles.pressableRowBody}
            onPress={handlePressRow}
          >
            <CompactRow
              badges={data.badges}
              deviceEventTimestamp={data.deviceEventTimestamp}
              deviceInstallationType={data.deviceInstallationType}
              deviceName={data.deviceName}
              isDark={isDark}
              runCompletionPct={data.runCompletion?.runCompletionPct}
              state={state}
            />
          </Pressable>
        </Box>
        <Pressable
          accessibilityLabel={chevronLabel}
          accessibilityRole={"button"}
          id={`deviceListItemCondensedChevron_${data.codaDeviceAlias}`}
          style={styles.pressableChevron}
          onPress={onToggleExpanded}
        >
          {isExpanded ? (
            <AppIcons.ChevronUp size={20} />
          ) : (
            <AppIcons.ChevronDown size={20} />
          )}
        </Pressable>
      </Row>

      <Collapse isOpen={isExpanded}>
        <ExpandedDetail
          data={data}
          showAdminInfo={showAdminInfo}
          showComment={showComment}
          onOpenProfile={onOpenProfile}
        />
      </Collapse>
    </View>
  )
}

interface ExpandedDetailProps {
  data: DeviceStatusData
  onOpenProfile: () => void
  showAdminInfo: boolean
  showComment: boolean
}

function ExpandedDetail({
  data,
  onOpenProfile,
  showAdminInfo,
  showComment,
}: ExpandedDetailProps): React.JSX.Element {
  const { t } = useTranslation("devices")
  const { unreachableUntil } = data
  const isUnreachable =
    typeof unreachableUntil === "string" &&
    new Date(unreachableUntil) > new Date()

  return (
    <Box px={"$3"} py={"$2"}>
      <Divider mb={"$2"} />

      <LastCheckInText
        deviceEventTimestamp={data.deviceEventTimestamp}
        fontSize={"$xs"}
      />

      {showAdminInfo ? (
        <Box mt={"$1"}>
          <AdminInfo
            deviceId={data.deviceId}
            firmwareVersion={data.firmwareVersion}
            hardwareGeneration={data.hardwareGeneration}
          />
        </Box>
      ) : null}

      {isUnreachable ? (
        <DeviceUnreachableWarning
          deviceId={data.deviceId}
          my={"$2"}
          unreachableUntil={unreachableUntil}
        />
      ) : null}

      {data.deviceStatus === "offline" ? (
        <Box my={"$2"}>
          <DeviceOfflineBadge />
        </Box>
      ) : (
        <Box mt={"$2"}>
          <StatusBadges
            badges={data.badges}
            deviceInstallationType={data.deviceInstallationType}
            deviceStatus={data.deviceStatus}
          />
        </Box>
      )}

      <BatteryCriticalWarning batteryState={data.batteryState} />

      <NextScheduledActionText
        deviceInstallationType={data.deviceInstallationType}
        nextScheduledAction={data.nextScheduledAction}
      />

      {data.runCompletion ? (
        <Row alignItems={"center"} mt={"$2"} space={"$2"} w={"$full"}>
          <Box flexGrow={1}>
            <RunProgressBar {...data.runCompletion} />
          </Box>
          {data.badges.runEta ? <StatusBadge {...data.badges.runEta} /> : null}
        </Row>
      ) : null}

      {showComment && data.commentActive ? (
        <Box mt={"$2"}>
          <DeviceCommentDisplay {...data.commentActive} />
        </Box>
      ) : null}

      <Row justifyContent={"flex-end"} mt={"$3"}>
        <Button
          IconComponent={"ListItemEnd"}
          iconPosition={"right"}
          id={`deviceProfileLink_${data.codaDeviceAlias}`}
          size={"sm"}
          text={t("deviceProfileLinkText")}
          variant={"outline"}
          onPress={onOpenProfile}
        />
      </Row>
    </Box>
  )
}

const styles = StyleSheet.create({
  asOfText: {
    marginTop: 2,
    textAlign: "right",
  },
  batteryCriticalText: {
    color: COLORS.$red[700],
  },
  batteryCriticalTextDark: {
    color: COLORS.$red[300],
  },
  compactBadge: {
    paddingHorizontal: SPACING.$1,
    paddingVertical: 0,
  },
  dot: {
    borderRadius: DOT_SIZE,
    height: DOT_SIZE,
    width: DOT_SIZE,
  },
  dotHollow: {
    borderWidth: 1.5,
  },
  nameColumn: {
    minWidth: 0,
  },
  nameText: {
    flexShrink: 1,
    fontWeight: "600",
  },
  pressableChevron: {
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: SPACING.$1,
    paddingVertical: ROW_VERTICAL_PADDING,
  },
  pressableRowBody: {
    paddingLeft: SPACING.$2,
    paddingRight: SPACING.$1,
    paddingVertical: ROW_VERTICAL_PADDING,
  },
  rightColumn: {
    // Allow the badge column to shrink so an over-wide badge set wraps
    // onto a second right-aligned line instead of pushing the row off
    // screen (the name column would otherwise truncate to nothing first).
    flexShrink: 1,
  },
  row: {
    borderRadius: 6,
  },
  rowSelected: {
    borderLeftWidth: 3,
  },
  rowUnselected: {
    // Reserve the same left-edge gutter so selected/unselected rows have
    // identical content alignment.
    borderLeftColor: COLORS.$transparent,
    borderLeftWidth: 3,
  },
  runPercentBadge: {
    backgroundColor: COLORS.$info[100],
    borderRadius: 4,
    paddingHorizontal: SPACING.$1,
    paddingVertical: 0,
  },
  runPercentBadgeText: {
    color: COLORS.$info[800],
    fontWeight: "600",
  },
  unconfiguredText: {
    color: COLORS.$amber[700],
  },
  unconfiguredTextDark: {
    color: COLORS.$amber[200],
  },
})
